import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, CalendarIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { Catalog, CatalogSchema, CatalogProduct, CatalogField, DataType } from '../CatalogsManagement';
interface ProductFormProps {
  product: CatalogProduct | null;
  catalog: Catalog;
  schema: CatalogSchema | null;
  onSave: (productData: Partial<CatalogProduct>) => void;
  onCancel: () => void;
  locale: string;
}
export function ProductForm({
  product,
  catalog,
  schema,
  onSave,
  onCancel,
  locale
}: ProductFormProps) {
  // Form state
  const [formData, setFormData] = useState<Partial<CatalogProduct>>({
    name: '',
    vendorSku: '',
    price: 0,
    currency: 'USD',
    availableStock: 0,
    manufacturer: '',
    category: '',
    hsCode: '',
    warranty: '',
    outerPackQty: 1,
    outerPackUnit: 'box',
    innerPackQty: 1,
    innerPackUnit: 'unit',
    backordered: 0,
    qtyOrdered: 0,
    qtyInvoiced: 0,
    activeFrom: new Date().toISOString().split('T')[0],
    activeTo: null,
    isActive: true,
    customAttributes: {}
  });
  // Available currencies
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'];
  // Common packaging units
  const packagingUnits = ['unit', 'box', 'case', 'pallet', 'pack', 'set', 'roll', 'sheet', 'bottle', 'carton'];
  // Initialize form with product data if editing
  useEffect(() => {
    if (product) {
      setFormData({
        ...product
      });
    } else {
      // Initialize custom attributes with default values from schema
      const defaultCustomAttributes: Record<string, any> = {};
      if (schema) {
        schema.fields.forEach(field => {
          defaultCustomAttributes[field.key] = field.defaultValue !== undefined ? field.defaultValue : getDefaultValueForType(field.dataType);
        });
      }
      setFormData(prev => ({
        ...prev,
        customAttributes: defaultCustomAttributes
      }));
    }
  }, [product, schema]);
  // Get default value for a data type
  const getDefaultValueForType = (dataType: DataType): any => {
    switch (dataType) {
      case 'string':
      case 'url':
        return '';
      case 'number':
      case 'currency':
        return 0;
      case 'boolean':
        return false;
      case 'date':
        return new Date().toISOString().split('T')[0];
      case 'dropdown':
        return '';
      default:
        return null;
    }
  };
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    // Handle checkbox
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle number inputs
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    // Remove non-numeric characters except decimal point
    const sanitizedValue = value.replace(/[^\d.]/g, '');
    const numberValue = sanitizedValue === '' ? '' : parseFloat(sanitizedValue);
    if (!isNaN(numberValue as number)) {
      setFormData(prev => ({
        ...prev,
        [name]: numberValue
      }));
    }
  };
  // Handle custom attribute changes
  const handleCustomAttributeChange = (key: string, value: any, dataType: DataType) => {
    // Convert value based on data type
    let processedValue: any = value;
    if (dataType === 'number' && value !== '') {
      processedValue = Number(value);
    } else if (dataType === 'boolean') {
      processedValue = value === 'true' || value === true;
    }
    setFormData(prev => ({
      ...prev,
      customAttributes: {
        ...prev.customAttributes,
        [key]: processedValue
      }
    }));
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  // Render input field based on data type
  const renderFieldInput = (field: CatalogField) => {
    const value = formData.customAttributes?.[field.key] ?? getDefaultValueForType(field.dataType);
    switch (field.dataType) {
      case 'string':
        return <input type="text" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} data-id="element-13732" />;
      case 'number':
        return <input type="number" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} step="any" data-id="element-13733" />;
      case 'boolean':
        return <select id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value.toString()} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} data-id="element-13734">
            <option value="true" data-id="element-13735">Yes</option>
            <option value="false" data-id="element-13736">No</option>
          </select>;
      case 'date':
        return <input type="date" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} data-id="element-13737" />;
      case 'dropdown':
        return <select id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} data-id="element-13738">
            <option value="" data-id="element-13739">Select an option</option>
            {field.options?.map(option => <option key={option} value={option} data-id="element-13740">
                {option}
              </option>)}
          </select>;
      case 'currency':
        return <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13741">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13742">
              <span className="text-gray-500 sm:text-sm" data-id="element-13743">$</span>
            </div>
            <input type="text" id={`custom-${field.key}`} name={`custom-${field.key}`} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} pattern="^\d*(\.\d{0,2})?$" placeholder="0.00" data-id="element-13744" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-13745">
              <span className="text-gray-500 sm:text-sm" data-id="element-13746">USD</span>
            </div>
          </div>;
      case 'url':
        return <input type="url" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, field.dataType)} required={field.isRequired} placeholder="https://example.com" data-id="element-13747" />;
      default:
        return <input type="text" id={`custom-${field.key}`} name={`custom-${field.key}`} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={value} onChange={e => handleCustomAttributeChange(field.key, e.target.value, 'string')} required={field.isRequired} data-id="element-13748" />;
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13749">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-13750">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13751">
          {product ? 'Edit Product' : 'Add Product'}
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13752">
          <XIcon className="h-5 w-5" data-id="element-13753" />
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-13754">
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-13755">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-13756">
            {/* Core Product Information */}
            <div className="sm:col-span-6" data-id="element-13757">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13758">
                Basic Information
              </h4>
            </div>
            {/* Product Name */}
            <div className="sm:col-span-4" data-id="element-13759">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-13760">
                Product Name
              </label>
              <div className="mt-1" data-id="element-13761">
                <input type="text" name="name" id="name" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.name} onChange={handleChange} required data-id="element-13762" />
              </div>
            </div>
            {/* Vendor SKU */}
            <div className="sm:col-span-2" data-id="element-13763">
              <label htmlFor="vendorSku" className="block text-sm font-medium text-gray-700" data-id="element-13764">
                Vendor SKU
              </label>
              <div className="mt-1" data-id="element-13765">
                <input type="text" name="vendorSku" id="vendorSku" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.vendorSku} onChange={handleChange} required data-id="element-13766" />
              </div>
            </div>
            {/* Price */}
            <div className="sm:col-span-2" data-id="element-13767">
              <label htmlFor="price" className="block text-sm font-medium text-gray-700" data-id="element-13768">
                Price
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13769">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13770">
                  <span className="text-gray-500 sm:text-sm" data-id="element-13771">$</span>
                </div>
                <input type="text" name="price" id="price" className="pl-7 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.price} onChange={handleNumberChange} required pattern="^\d*(\.\d{0,2})?$" placeholder="0.00" data-id="element-13772" />
              </div>
            </div>
            {/* Currency */}
            <div className="sm:col-span-2" data-id="element-13773">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-13774">
                Currency
              </label>
              <div className="mt-1" data-id="element-13775">
                <select id="currency" name="currency" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.currency} onChange={handleChange} required data-id="element-13776">
                  {currencies.map(currency => <option key={currency} value={currency} data-id="element-13777">
                      {currency}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Available Stock */}
            <div className="sm:col-span-2" data-id="element-13778">
              <label htmlFor="availableStock" className="block text-sm font-medium text-gray-700" data-id="element-13779">
                Available Stock
              </label>
              <div className="mt-1" data-id="element-13780">
                <input type="number" name="availableStock" id="availableStock" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.availableStock} onChange={handleChange} required min="0" data-id="element-13781" />
              </div>
            </div>
            {/* Manufacturer */}
            <div className="sm:col-span-3" data-id="element-13782">
              <label htmlFor="manufacturer" className="block text-sm font-medium text-gray-700" data-id="element-13783">
                Manufacturer
              </label>
              <div className="mt-1" data-id="element-13784">
                <input type="text" name="manufacturer" id="manufacturer" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.manufacturer} onChange={handleChange} required data-id="element-13785" />
              </div>
            </div>
            {/* Category */}
            <div className="sm:col-span-3" data-id="element-13786">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700" data-id="element-13787">
                Category
              </label>
              <div className="mt-1" data-id="element-13788">
                <input type="text" name="category" id="category" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.category} onChange={handleChange} required data-id="element-13789" />
              </div>
            </div>
            {/* Additional Information */}
            <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13790">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13791">
                Additional Information
              </h4>
            </div>
            {/* HS Code */}
            <div className="sm:col-span-3" data-id="element-13792">
              <label htmlFor="hsCode" className="block text-sm font-medium text-gray-700" data-id="element-13793">
                HS Code
              </label>
              <div className="mt-1" data-id="element-13794">
                <input type="text" name="hsCode" id="hsCode" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.hsCode} onChange={handleChange} data-id="element-13795" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-13796">
                Harmonized System code for customs classification
              </p>
            </div>
            {/* Warranty */}
            <div className="sm:col-span-3" data-id="element-13797">
              <label htmlFor="warranty" className="block text-sm font-medium text-gray-700" data-id="element-13798">
                Warranty
              </label>
              <div className="mt-1" data-id="element-13799">
                <input type="text" name="warranty" id="warranty" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.warranty} onChange={handleChange} placeholder="e.g., 1 year" data-id="element-13800" />
              </div>
            </div>
            {/* Packaging Information */}
            <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13801">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13802">
                Packaging Information
              </h4>
            </div>
            {/* Outer Pack Quantity */}
            <div className="sm:col-span-1" data-id="element-13803">
              <label htmlFor="outerPackQty" className="block text-sm font-medium text-gray-700" data-id="element-13804">
                Outer Pack Qty
              </label>
              <div className="mt-1" data-id="element-13805">
                <input type="number" name="outerPackQty" id="outerPackQty" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.outerPackQty} onChange={handleChange} min="1" data-id="element-13806" />
              </div>
            </div>
            {/* Outer Pack Unit */}
            <div className="sm:col-span-2" data-id="element-13807">
              <label htmlFor="outerPackUnit" className="block text-sm font-medium text-gray-700" data-id="element-13808">
                Outer Pack Unit
              </label>
              <div className="mt-1" data-id="element-13809">
                <select id="outerPackUnit" name="outerPackUnit" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.outerPackUnit} onChange={handleChange} data-id="element-13810">
                  {packagingUnits.map(unit => <option key={unit} value={unit} data-id="element-13811">
                      {unit}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Inner Pack Quantity */}
            <div className="sm:col-span-1" data-id="element-13812">
              <label htmlFor="innerPackQty" className="block text-sm font-medium text-gray-700" data-id="element-13813">
                Inner Pack Qty
              </label>
              <div className="mt-1" data-id="element-13814">
                <input type="number" name="innerPackQty" id="innerPackQty" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.innerPackQty} onChange={handleChange} min="1" data-id="element-13815" />
              </div>
            </div>
            {/* Inner Pack Unit */}
            <div className="sm:col-span-2" data-id="element-13816">
              <label htmlFor="innerPackUnit" className="block text-sm font-medium text-gray-700" data-id="element-13817">
                Inner Pack Unit
              </label>
              <div className="mt-1" data-id="element-13818">
                <select id="innerPackUnit" name="innerPackUnit" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.innerPackUnit} onChange={handleChange} data-id="element-13819">
                  {packagingUnits.map(unit => <option key={unit} value={unit} data-id="element-13820">
                      {unit}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Inventory Information */}
            <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13821">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13822">
                Inventory Information
              </h4>
            </div>
            {/* Backordered */}
            <div className="sm:col-span-2" data-id="element-13823">
              <label htmlFor="backordered" className="block text-sm font-medium text-gray-700" data-id="element-13824">
                Backordered Quantity
              </label>
              <div className="mt-1" data-id="element-13825">
                <input type="number" name="backordered" id="backordered" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.backordered} onChange={handleChange} min="0" data-id="element-13826" />
              </div>
            </div>
            {/* Quantity Ordered */}
            <div className="sm:col-span-2" data-id="element-13827">
              <label htmlFor="qtyOrdered" className="block text-sm font-medium text-gray-700" data-id="element-13828">
                Quantity Ordered
              </label>
              <div className="mt-1" data-id="element-13829">
                <input type="number" name="qtyOrdered" id="qtyOrdered" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.qtyOrdered} onChange={handleChange} min="0" data-id="element-13830" />
              </div>
            </div>
            {/* Quantity Invoiced */}
            <div className="sm:col-span-2" data-id="element-13831">
              <label htmlFor="qtyInvoiced" className="block text-sm font-medium text-gray-700" data-id="element-13832">
                Quantity Invoiced
              </label>
              <div className="mt-1" data-id="element-13833">
                <input type="number" name="qtyInvoiced" id="qtyInvoiced" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.qtyInvoiced} onChange={handleChange} min="0" data-id="element-13834" />
              </div>
            </div>
            {/* Lifecycle Information */}
            <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13835">
              <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13836">
                Lifecycle Information
              </h4>
            </div>
            {/* Active Status */}
            <div className="sm:col-span-6 flex items-center" data-id="element-13837">
              <div className="flex h-5 items-center" data-id="element-13838">
                <input id="isActive" name="isActive" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={formData.isActive} onChange={handleChange} data-id="element-13839" />
              </div>
              <div className="ml-3 text-sm" data-id="element-13840">
                <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-13841">
                  Active
                </label>
                <p className="text-gray-500" data-id="element-13842">
                  Make this product available for use
                </p>
              </div>
            </div>
            {/* Active From */}
            <div className="sm:col-span-3" data-id="element-13843">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-13844">
                Active From
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13845">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13846">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-13847" />
                </div>
                <input type="date" name="activeFrom" id="activeFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeFrom} onChange={handleChange} required data-id="element-13848" />
              </div>
            </div>
            {/* Active To */}
            <div className="sm:col-span-3" data-id="element-13849">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-13850">
                Active To
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-13851">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13852">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-13853" />
                </div>
                <input type="date" name="activeTo" id="activeTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeTo || ''} onChange={handleChange} data-id="element-13854" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-13855">
                Leave blank for no end date
              </p>
            </div>
            {/* Custom Attributes */}
            {schema && schema.fields.length > 0 && <>
                <div className="sm:col-span-6 pt-4 border-t border-gray-200" data-id="element-13856">
                  <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13857">
                    Custom Attributes
                  </h4>
                </div>
                {schema.fields.map(field => <div key={field.id} className="sm:col-span-3" data-id="element-13858">
                    <label htmlFor={`custom-${field.key}`} className="block text-sm font-medium text-gray-700" data-id="element-13859">
                      {field.name}
                      {field.isRequired && <span className="text-red-500" data-id="element-13860">*</span>}
                    </label>
                    <div className="mt-1" data-id="element-13861">{renderFieldInput(field)}</div>
                  </div>)}
              </>}
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-13862">
          <button type="button" className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={onCancel} data-id="element-13863">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13864">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-13865" />
            {product ? 'Update Product' : 'Add Product'}
          </button>
        </div>
      </form>
    </div>;
}